/**
 * AI Assistant Service
 * 
 * Handles all API calls for the AI Assistant feature:
 * - Conversations (CRUD, bookmarking)
 * - Messages (send, fetch)
 * - Saved Prompts (CRUD)
 */

import { FetchUtils } from '../utils/fetch-utils.js';
import { streamMessage } from './llm-sse-stream.js';

export class AIAssistantService {
  // ==========================================
  // CONVERSATIONS
  // ==========================================

  /**
   * Fetch list of conversations
   * @param {Object} params - Query params (limit, skip)
   * @returns {Promise<{conversations: Array, totalCount: number}>}
   */
  static async getConversations(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const url = `/api/llm/conversations${queryString ? `?${queryString}` : ''}`;
    const response = await FetchUtils.get(url);
    return response; // { conversations, totalCount }
  }

  /**
   * Get a single conversation by ID
   * @param {string} conversationId 
   * @returns {Promise<Object>}
   */
  static async getConversation(conversationId) {
    const response = await FetchUtils.get(`/api/llm/conversations/${conversationId}`);
    return response.conversation; // Unwrap { conversation }
  }

  /**
   * Create a new conversation
   * @param {Object} data - { title?: string }
   * @returns {Promise<Object>}
   */
  static async createConversation(data = {}) {
    const response = await FetchUtils.post('/api/llm/conversations', data);
    return response.conversation; // Unwrap { conversation }
  }

  /**
   * Update conversation title
   * @param {string} conversationId 
   * @param {string} title 
   * @returns {Promise<Object>}
   */
  static async updateConversationTitle(conversationId, title) {
    const response = await FetchUtils.patch(`/api/llm/conversations/${conversationId}`, { title });
    return response.conversation; // Unwrap { conversation }
  }

  /**
   * Delete a conversation
   * @param {string} conversationId 
   * @returns {Promise<Object>}
   */
  static async deleteConversation(conversationId) {
    return FetchUtils.delete(`/api/llm/conversations/${conversationId}`);
  }

  /**
   * Get bookmarked conversations
   * @param {Object} params - Query params (limit, skip)
   * @returns {Promise<{conversations: Array, totalCount: number}>}
   */
  static async getBookmarkedConversations(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const url = `/api/llm/conversations/bookmarked/list${queryString ? `?${queryString}` : ''}`;
    const response = await FetchUtils.get(url);
    return response; // { conversations, totalCount }
  }

  /**
   * Bookmark a conversation
   * @param {string} conversationId 
   * @returns {Promise<Object>}
   */
  static async bookmarkConversation(conversationId) {
    const response = await FetchUtils.post(`/api/llm/conversations/${conversationId}/bookmark`, {});
    return response.conversation; // Unwrap { conversation }
  }

  /**
   * Remove bookmark from conversation
   * @param {string} conversationId 
   * @returns {Promise<Object>}
   */
  static async unbookmarkConversation(conversationId) {
    const response = await FetchUtils.delete(`/api/llm/conversations/${conversationId}/bookmark`);
    return response.conversation; // Unwrap { conversation }
  }

  // ==========================================
  // MESSAGES
  // ==========================================

  /**
   * Send a message to a conversation
   * If conversationId is null, a new conversation will be created
   * @param {Object} data - { conversationId?: string, message: string }
   * @param {AbortSignal} signal - Optional abort signal
   * @returns {Promise<{conversationId: string, userMessage: Object, assistantMessage: Object}>}
   */
  static async sendMessage(data, signal = null) {
    const body = {
      message: data.message || data.content,
      ...(data.conversationId && { conversationId: data.conversationId })
    };
    return FetchUtils.post('/api/llm/messages', body, signal);
  }

  /**
   * Stream a message via SSE (POST /api/llm/messages).
   * @param {Object} data
   * @param {string} data.message
   * @param {string} [data.conversationId]
   * @param {string} [data.listId]
   * @param {{ entityType: string, entityId: string }} [data.focus]
   * @param {string} [data.action]
   * @param {string} [data.proposalId]
   * @param {function} data.onUpdate - Called with { content, parts, references }
   * @param {function} [data.onError]
   * @param {AbortSignal} [signal]
   */
  static streamMessage(data, signal = null) {
    return streamMessage({
      message: data.message || data.content,
      conversationId: data.conversationId,
      listId: data.listId,
      focus: data.focus,
      action: data.action,
      proposalId: data.proposalId,
      onUpdate: data.onUpdate,
      onError: data.onError,
      signal,
    });
  }

  /**
   * Fetch a single message by ID (fallback when stream result is incomplete).
   */
  static async getMessage(conversationId, messageId) {
    const response = await this.getMessages(conversationId, { limit: 30 });
    const messages = response.messages || [];
    return messages.find((m) => m._id === messageId) || null;
  }

  /**
   * Get messages for a conversation
   * @param {string} conversationId 
   * @param {Object} params - Query params (limit, skip)
   * @returns {Promise<{messages: Array, totalCount: number}>}
   */
  static async getMessages(conversationId, params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const url = `/api/llm/conversations/${conversationId}/messages${queryString ? `?${queryString}` : ''}`;
    const response = await FetchUtils.get(url);
    return response; // { messages, totalCount }
  }

  // ==========================================
  // SAVED PROMPTS
  // ==========================================

  /**
   * Get saved prompts
   * @param {Object} params - Query params (category, isFavorite)
   * @returns {Promise<{prompts: Array, totalCount: number}>}
   */
  static async getSavedPrompts(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const url = `/api/llm/saved-prompts${queryString ? `?${queryString}` : ''}`;
    const response = await FetchUtils.get(url);
    return response; // { prompts, totalCount }
  }

  /**
   * Get a single saved prompt
   * @param {string} promptId 
   * @returns {Promise<Object>}
   */
  static async getSavedPrompt(promptId) {
    const response = await FetchUtils.get(`/api/llm/saved-prompts/${promptId}`);
    return response.prompt; // Unwrap { prompt }
  }

  /**
   * Create a saved prompt
   * @param {Object} data - { title, prompt, description?, category?, isFavorite?, isShared? }
   * @returns {Promise<Object>}
   */
  static async createSavedPrompt(data) {
    const response = await FetchUtils.post('/api/llm/saved-prompts', data);
    return response.prompt; // Unwrap { prompt }
  }

  /**
   * Update a saved prompt
   * @param {string} promptId 
   * @param {Object} data 
   * @returns {Promise<Object>}
   */
  static async updateSavedPrompt(promptId, data) {
    const response = await FetchUtils.patch(`/api/llm/saved-prompts/${promptId}`, data);
    return response.prompt; // Unwrap { prompt }
  }

  /**
   * Delete a saved prompt
   * @param {string} promptId 
   * @returns {Promise<Object>}
   */
  static async deleteSavedPrompt(promptId) {
    return FetchUtils.delete(`/api/llm/saved-prompts/${promptId}`);
  }

  // ==========================================
  // FEEDBACK
  // ==========================================

  /**
   * Submit per-message feedback (thumbs up/down).
   * @param {Object} data - { conversationId, messageId, rating: 1|-1, note? }
   */
  static async submitMessageFeedback(data) {
    return FetchUtils.post('/api/llm/feedback', {
      type: 'message',
      ...data,
    });
  }

  /**
   * Submit conversation-level star rating (1–5).
   * @param {Object} data - { conversationId, rating: 1..5, note? }
   */
  static async submitConversationFeedback(data) {
    return FetchUtils.post('/api/llm/feedback', {
      type: 'conversation',
      ...data,
    });
  }

  static async getMessageFeedback(messageId) {
    return FetchUtils.get(`/api/llm/feedback/message/${messageId}`);
  }

  static async getConversationFeedback(conversationId) {
    return FetchUtils.get(`/api/llm/feedback/conversation/${conversationId}`);
  }
}
